import { useEffect, useState } from "react";
import { Save, Store, Palette, ScrollText, Upload, Image as ImageIcon, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fileUrl } from "../../lib/api";
import { PageHeader, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function StoreSettings() {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/store-admin/settings").then((r) => setW(r.data)); }, []);
  if (!w) return <LoadingRows />;

  const info = w.business_info || {};
  const st = w.settings || {};
  const colors = st.colors || {};

  const save = async () => {
    const wa = (st.whatsapp_number || "").trim();
    if (wa && !/^\+?[0-9]{8,15}$/.test(wa)) { toast.error("Invalid WhatsApp number — use country code + digits, e.g. +919876543210"); return; }
    setBusy(true);
    try {
      await api.patch("/store-admin/settings", { name: w.name, business_info: info, settings: st });
      toast.success("Store settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const setInfo = (k, v) => setW({ ...w, business_info: { ...info, [k]: v } });
  const setSt = (k, v) => setW({ ...w, settings: { ...st, [k]: v } });
  const setColor = (k, v) => setW({ ...w, settings: { ...st, colors: { ...colors, [k]: v } } });
  const legal = st.legal || {};
  const setLegal = (k, v) => setSt("legal", { ...legal, [k]: v });
  const pay = { gateway_enabled: true, whatsapp_enabled: true, upi_enabled: false, upi_id: "", upi_name: "", upi_qr: "", ...(st.payments || {}) };
  const setPay = (k, v) => setSt("payments", { ...pay, [k]: v });

  const uploadAsset = async (e, cb) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      cb(data.url);
      toast.success("Uploaded — click Save Changes to apply");
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  const LEGAL_FIELDS = [
    ["privacy_policy", "Privacy Policy"], ["terms", "Terms & Conditions"], ["refund_policy", "Refund / Return Policy"],
    ["shipping_policy", "Shipping / Delivery Policy"], ["contact_page", "Contact / Business Information"],
  ];

  return (
    <div data-testid="store-settings">
      <PageHeader title="Store Settings" subtitle="Business profile, checkout rules and look & feel.">
        <Button data-testid="store-settings-save" onClick={save} disabled={busy} className="rounded-full font-bold active:scale-95 transition-transform">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save Changes"}
        </Button>
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="bg-card border border-border rounded-md p-8 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><Store className="h-4 w-4" /></div>
            <p className="font-display font-bold text-lg">Business Profile</p>
          </div>
          <div className="space-y-1.5"><Label className="overline-label">Description</Label>
            <Input data-testid="set-description" value={info.description || ""} onChange={(e) => setInfo("description", e.target.value)} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="overline-label">Phone</Label>
              <Input data-testid="set-phone" value={info.phone || ""} onChange={(e) => setInfo("phone", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Email</Label>
              <Input data-testid="set-email" value={info.email || ""} onChange={(e) => setInfo("email", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label className="overline-label">Address</Label>
            <Input data-testid="set-address" value={info.address || ""} onChange={(e) => setInfo("address", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="overline-label">WhatsApp Order Number</Label>
            <Input data-testid="set-whatsapp" value={st.whatsapp_number || ""} onChange={(e) => setSt("whatsapp_number", e.target.value)} placeholder="+919876543210" className="font-mono" />
            <p className="text-xs text-muted-foreground">Customers can send their order to this WhatsApp number from checkout. Include country code.</p></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="overline-label">Shipping fee (₹)</Label>
              <Input data-testid="set-shipping" type="number" value={st.shipping_fee ?? 0} onChange={(e) => setSt("shipping_fee", Number(e.target.value))} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Tax (%)</Label>
              <Input data-testid="set-tax" type="number" value={st.tax_percent ?? 0} onChange={(e) => setSt("tax_percent", Number(e.target.value))} /></div>
          </div>
          <div className="space-y-1.5"><Label className="overline-label">Storefront mode</Label>
            <Select value={st.mode || "light"} onValueChange={(v) => setSt("mode", v)}>
              <SelectTrigger data-testid="set-mode"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="light">Light</SelectItem><SelectItem value="dark">Dark</SelectItem></SelectContent>
            </Select></div>
        </div>

        <div className="bg-card border border-border rounded-md p-8 space-y-4" data-testid="website-branding-section">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><ImageIcon className="h-4 w-4" /></div>
            <div><p className="font-display font-bold text-lg">Website Branding</p>
              <p className="text-xs text-muted-foreground">Your store's own identity — the platform brand is never shown on your website.</p></div>
          </div>
          <div className="space-y-1.5"><Label className="overline-label">Website Name</Label>
            <Input data-testid="brand-site-name" value={w.name || ""} onChange={(e) => setW({ ...w, name: e.target.value })} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="overline-label">Business Logo</Label>
              {info.logo_url && <img src={fileUrl(info.logo_url)} alt="" className="h-12 object-contain rounded-md border border-border p-1.5 bg-white" data-testid="brand-logo-preview" />}
              <label className="h-10 px-4 rounded-md border border-border text-xs font-bold flex items-center gap-1.5 cursor-pointer hover:bg-muted w-fit transition-colors" data-testid="brand-logo-upload">
                <Upload className="h-3.5 w-3.5" /> Upload logo
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset(e, (url) => setInfo("logo_url", url))} />
              </label>
              <p className="text-[11px] text-muted-foreground">No logo uploaded? Your store name is shown as clean text instead.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Favicon</Label>
              {st.favicon_url && <img src={fileUrl(st.favicon_url)} alt="" className="h-8 w-8 object-contain rounded-md border border-border p-1 bg-white" data-testid="brand-favicon-preview" />}
              <label className="h-10 px-4 rounded-md border border-border text-xs font-bold flex items-center gap-1.5 cursor-pointer hover:bg-muted w-fit transition-colors" data-testid="brand-favicon-upload">
                <Upload className="h-3.5 w-3.5" /> Upload favicon
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset(e, (url) => setSt("favicon_url", url))} />
              </label>
              <p className="text-[11px] text-muted-foreground">Shown in the browser tab for your store pages only.</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-8 space-y-5" data-testid="payment-settings-section">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><Wallet className="h-4 w-4" /></div>
            <div><p className="font-display font-bold text-lg">Checkout / Payment Settings</p>
              <p className="text-xs text-muted-foreground">Only enabled methods appear on your checkout page.</p></div>
          </div>
          {[
            ["gateway_enabled", "Online Payment Gateway", "Cards, netbanking & UPI through the configured payment gateway."],
            ["whatsapp_enabled", "WhatsApp Ordering", "Customers send the full order to your WhatsApp Order Number above."],
            ["upi_enabled", "UPI / QR Payment", "Show your own UPI ID and QR code at checkout for direct payments."],
          ].map(([k, label, hint]) => (
            <div key={k} className="flex items-center justify-between gap-4 border border-border rounded-md p-4" data-testid={`pay-toggle-row-${k}`}>
              <div><p className="text-sm font-bold">{label}</p><p className="text-xs text-muted-foreground">{hint}</p></div>
              <Switch data-testid={`pay-toggle-${k}`} checked={!!pay[k]} onCheckedChange={(v) => setPay(k, v)} />
            </div>
          ))}
          {pay.upi_enabled && (
            <div className="space-y-3 border border-border rounded-md p-4" data-testid="upi-config">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="overline-label">Payment Name (Payee)</Label>
                  <Input data-testid="upi-name" value={pay.upi_name} onChange={(e) => setPay("upi_name", e.target.value)} placeholder={w.name || "My Store"} /></div>
                <div className="space-y-1.5"><Label className="overline-label">UPI ID</Label>
                  <Input data-testid="upi-id" value={pay.upi_id} onChange={(e) => setPay("upi_id", e.target.value)} placeholder="store@okhdfcbank" className="font-mono" /></div>
              </div>
              <div className="space-y-1.5"><Label className="overline-label">UPI QR Code</Label>
                {pay.upi_qr && <img src={fileUrl(pay.upi_qr)} alt="UPI QR" className="h-28 w-28 object-contain rounded-md border border-border bg-white p-2" data-testid="upi-qr-preview" />}
                <label className="h-10 px-4 rounded-md border border-border text-xs font-bold flex items-center gap-1.5 cursor-pointer hover:bg-muted w-fit transition-colors" data-testid="upi-qr-upload">
                  <Upload className="h-3.5 w-3.5" /> Upload QR
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset(e, (url) => setPay("upi_qr", url))} />
                </label>
                <p className="text-[11px] text-muted-foreground">This QR belongs to this website only and is shown on its checkout page.</p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-md p-8" data-testid="theme-customization-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><Palette className="h-4 w-4" /></div>
            <div><p className="font-display font-bold text-lg">Theme Customization</p>
              <p className="text-xs text-muted-foreground">Current theme: {w.theme?.name || "—"}</p></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["primary", "Primary"], ["secondary", "Secondary"], ["accent", "Accent"], ["bg", "Background"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label className="overline-label">{l}</Label>
                <div className="flex items-center gap-2 border border-border rounded-md p-1.5">
                  <input type="color" data-testid={`color-${k}`} value={colors[k] || "#000000"} onChange={(e) => setColor(k, e.target.value)} className="h-8 w-10 cursor-pointer bg-transparent" />
                  <span className="text-xs text-muted-foreground font-mono">{colors[k]}</span>
                </div>
              </div>
            ))}
          </div>
          {w.theme?.preview && (
            <div className="mt-6 rounded-md overflow-hidden border border-border">
              <img src={w.theme.preview} alt="theme" className="w-full aspect-[16/8] object-cover" />
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">Color changes apply to your live storefront instantly after saving.</p>
        </div>

        <div className="bg-card border border-border rounded-md p-8 lg:col-span-2" data-testid="legal-pages-section">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center"><ScrollText className="h-4 w-4" /></div>
            <div><p className="font-display font-bold text-lg">Legal Pages</p>
              <p className="text-xs text-muted-foreground">Pages with content automatically appear in your store's footer, styled by your theme.</p></div>
          </div>
          <div className="grid lg:grid-cols-2 gap-4 mt-4">
            {LEGAL_FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="overline-label">{label}</Label>
                <Textarea data-testid={`legal-${k}`} rows={5} value={legal[k] || ""} onChange={(e) => setLegal(k, e.target.value)}
                  placeholder={`Write your ${label.toLowerCase()} here…`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
